/**
 * Global viewport state manager for OrgGraphVis
 * Provides fast, bare-bone viewport state preservation before search term submission
 * and restoration after graph updates, independent of node filters.
 */

export interface ViewportState {
  position: { x: number; y: number };
  scale: number;
  timestamp: number;
}

class ViewportStateManager {
  private static instance: ViewportStateManager;
  private savedViewportState: ViewportState | null = null;

  private constructor() {}

  public static getInstance(): ViewportStateManager {
    if (!ViewportStateManager.instance) {
      ViewportStateManager.instance = new ViewportStateManager();
    }
    return ViewportStateManager.instance;
  }

  /**
   * Capture the current viewport state from the vis network
   * @param network - The vis Network instance
   */
  public captureViewport(network: any): void {
    if (!network) return;

    try {
      const position = network.getViewPosition();
      const scale = network.getScale();
      
      this.savedViewportState = {
        position: { x: position.x, y: position.y },
        scale,
        timestamp: Date.now()
      };
    } catch (error) {
      console.warn('Failed to capture viewport:', error);
    }
  }

  /**
   * Restore the saved viewport state to the vis network
   * @param network - The vis Network instance
   */
  public restoreViewport(network: any): void {
    if (!network || !this.savedViewportState) return;

    try {
      // Use moveTo with animation disabled for fast restoration
      network.moveTo({
        position: this.savedViewportState.position,
        scale: this.savedViewportState.scale,
        animation: false // Disable animation for instant restoration
      });
    } catch (error) {
      console.warn('Failed to restore viewport:', error);
    }
  }

  /**
   * Fast restore using setViewPosition and setScale directly (no animation)
   * @param network - The vis Network instance
   */
  public restoreViewportInstant(network: any): void {
    if (!network || !this.savedViewportState) return;

    try {
      // Use direct methods for even faster restoration
      network.setViewPosition(this.savedViewportState.position.x, this.savedViewportState.position.y);
      network.setScale(this.savedViewportState.scale);
    } catch (error) {
      console.warn('Failed to restore viewport instantly:', error);
    }
  }

  /**
   * Check if we have a saved viewport state
   */
  public hasSavedViewport(): boolean {
    return this.savedViewportState !== null;
  }

  /**
   * Clear the saved viewport state
   */
  public clearSavedViewport(): void {
    this.savedViewportState = null;
  }

  /**
   * Get the saved viewport state (for debugging)
   */
  public getSavedViewport(): ViewportState | null {
    return this.savedViewportState;
  }
}

export const viewportStateManager = ViewportStateManager.getInstance();
